import { useState, useEffect } from "react"
import { Modal, Table, Dropdown } from "react-bootstrap"
import { CKEditor } from "@ckeditor/ckeditor5-react"
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import { toast } from "sonner"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes, faXmark } from "@fortawesome/free-solid-svg-icons"
import { GetRequest, PostRequest } from "../../../Utils/hooks/ApiHandler"
import ReactQuill from "react-quill"

export const EmailModal = ({
  show,
  onClose,
  id,
  pipelineId,
  customerId,
  loginId,
  headers,
  extras,
  data,
  module_name
}) => {
  // Integration modal state
  const [showIntegrationModal, setShowIntegrationModal] = useState(false)

  // SMTP details state
  const [smtpDetails, setSmtpDetails] = useState(null)
  const [integrationLoading, setIntegrationLoading] = useState(false)

  // Templates state
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState("")
  const [templatesLoading, setTemplatesLoading] = useState(false)

  // Email recipients state (tag input)
  const [emailRecipients, setEmailRecipients] = useState([])
  const [currentEmailInput, setCurrentEmailInput] = useState("")
  const [emailInputTouched, setEmailInputTouched] = useState(false)

  // Message data state
  const [messageData, setMessageData] = useState({
    subject: "",
    body: "",
    file: null,
    notes: "",
  })

  // Loading state for sending email
  const [dataSendingLoader, setDataSendingLoader] = useState(false)

  /**
   * Check SMTP integration when modal opens
   */
  useEffect(() => {
    if (show) {
      checkSmtpIntegration()
      fetchTemplates()
      // Pre-populate contact emails if available
      if (data?.email) {
        setEmailRecipients([data.email])
      }
    }
  }, [show, data])

  /**
   * Check SMTP integration status
   */
  const checkSmtpIntegration = async () => {
    setIntegrationLoading(true)
    try {
      const response = await GetRequest(`common/fetchSMTPDetails/${customerId}/leads`, { headers }, extras)
      if (response.status === "success") {
        setSmtpDetails(response.data)
        setTemplates(response.data)
        // If integration is successful, modal will show normally
      } else {
        // Show integration modal if failed
        setShowIntegrationModal(true)
        onClose() // Close email modal
      }
    } catch (error) {
      setShowIntegrationModal(true)
      onClose()
    } finally {
      setIntegrationLoading(false)
    }
  }

  /**
   * Fetch email templates
   */
  const fetchTemplates = async () => {
    setTemplatesLoading(true)
    try {
      const response = await GetRequest(`common/fetchEmailTemplates/${customerId}`, { headers }, extras)
      if (response.status === "success" && response.data) {
        setTemplates(response.data)
      }
    } catch (error) {
      console.error("Error fetching templates:", error)
    } finally {
      setTemplatesLoading(false)
    }
  }

  /**
   * Handle template selection with toggle functionality
   */
  const handleTemplateSelect = async (templateId, templateName) => {
    // If same template is selected, clear the data
    if (selectedTemplate === templateName) {
      setSelectedTemplate("")
      setMessageData((prev) => ({
        ...prev,
        subject: "",
        body: "",
      }))
      return
    }

    if (!templateId) {
      setSelectedTemplate("Choose Template")
      setMessageData((prev) => ({
        ...prev,
        subject: "",
        body: "",
      }))
      return
    }

    setSelectedTemplate(templateName)

    try {
      const response = await GetRequest(`common/getEmailTemplate/${templateId}/${customerId}`, { headers }, extras)

      if (response.status === "success" && response.data) {
        setMessageData((prev) => ({
          ...prev,
          subject: response.data.subject || "",
          body: response.data.body || "",
        }))
      }
    } catch (error) {
      toast.error("Error loading template")
    }
  }

  /**
   * Handle email input for tag system
   */
  const handleEmailInputKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      setEmailInputTouched(true)
      addEmailTag()
    }
  }

  /**
   * Handle email input blur with validation
   */
  const handleEmailInputBlur = () => {
    if (emailInputTouched && currentEmailInput.trim()) {
      addEmailTag()
    }
  }

  /**
   * Add email tag with validation
   */
  const addEmailTag = () => {
    const email = currentEmailInput.trim()
    if (email && isValidEmail(email) && !emailRecipients.includes(email)) {
      setEmailRecipients((prev) => [...prev, email])
      setCurrentEmailInput("")
      setEmailInputTouched(false)
    } else if (email && !isValidEmail(email)) {
      toast.error("Please enter a valid email address")
    }
  }

  /**
   * Remove email tag
   */
  const removeEmailTag = (emailToRemove) => {
    setEmailRecipients((prev) => prev.filter((email) => email !== emailToRemove))
  }

  /**
   * Validate email format
   */
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  /**
   * Handle form input changes
   */
  const handleMessageInputChange = (e) => {
    const { name, value } = e.target
    setMessageData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  /**
   * Handle file attachment
   */
  const handleFileChange = (e) => {
    setMessageData((prev) => ({
      ...prev,
      file: e.target.files[0],
    }))
  }

  /**
   * Handle CKEditor body change
   */
  const handleBodyChange = (event, editor) => {
    const data = editor.getData()
    setMessageData((prev) => ({
      ...prev,
      body: data,
    }))
  }

  /**
   * Handle form submission and send email
   */
  const handleSendMessage = async () => {
    // Validation
    if (emailRecipients.length === 0) {
      toast.error("Please add at least one email recipient")
      return
    }

    if (!messageData.subject.trim()) {
      toast.error("Please enter email subject")
      return
    }

    if (!messageData.body.trim()) {
      toast.error("Please enter email body")
      return
    }

    setDataSendingLoader(true)

    try {
      const payload = {
        customer_id: customerId,
        to_id: id,
        user_email: emailRecipients,
        email_document: messageData.file,
        subject: messageData.subject,
        body: messageData.body,
        module_name: module_name || "leads",
        user_login_id: loginId,
        pipeline_id: pipelineId,
      }

      const response = await PostRequest(
        "common/sendMail",
        payload,
        true, // Set to true for file upload
        { headers },
        extras,
      )

      if (response.status === "success") {
        toast.success("Email sent successfully")
        handleCloseModal()
      } else {
        toast.error(response.message || "Failed to send email")
      }
    } catch (error) {
      toast.error(error.message || "Error sending email")
    } finally {
      setDataSendingLoader(false)
    }
  }

  /**
   * Handle modal close and reset form
   */
  const handleCloseModal = () => {
    setMessageData({
      subject: "",
      body: "",
      file: null,
      notes: "",
    })
    setEmailRecipients([])
    setCurrentEmailInput("")
    setSelectedTemplate("")
    setEmailInputTouched(false)
    onClose()
  }

  /**
   * Handle integration modal close
   */
  const handleIntegrationModalClose = () => {
    setShowIntegrationModal(false)
  }

  if (integrationLoading) {
    return (
      <Modal show={show} onHide={onClose} centered>
        <Modal.Body className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Checking integration...</span>
          </div>
          <p className="mt-2">Checking email integration...</p>
        </Modal.Body>
      </Modal>
    )
  }

  return (
    <>
      {/* Loading overlay */}
      {dataSendingLoader && (
        <div
          className="d-flex justify-content-center align-items-center position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
          style={{ zIndex: 1000 }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Sending email...</span>
          </div>
        </div>
      )}

      {/* Email Modal */}
      <Modal show={show && !showIntegrationModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header>
          <Modal.Title className="d-flex justify-content-between align-items-center w-100">
            <span>Send Email</span>
            {/* Template Dropdown */}
            <div className="d-flex align-items-center gap-2">
              <Dropdown>
                <Dropdown.Toggle variant="outline-primary" size="sm" disabled={templatesLoading}>
                  {templatesLoading ? "Loading..." : selectedTemplate || "Select Template"}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleTemplateSelect("", "Choose Template")}>Choose Template</Dropdown.Item>
                  {templates.map((template) => (
                    <Dropdown.Item
                      key={template.id}
                      onClick={() => handleTemplateSelect(template.id, template.template_name)}
                    >
                      {template.template_name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Modal.Title>
          <p className='mb-0 mx-2 ' style={{ borderRight: '1px solid #c9c9c9', height: 29, cursor: 'pointer' }}><FontAwesomeIcon icon={faXmark} onClick={handleCloseModal} /></p>

        </Modal.Header>
        <Modal.Body>
          {/* To Field with Tag Input */}
          <div className="mb-3">
            <label className="form-label">To</label>
            <div className="border rounded p-2" style={{ minHeight: "45px" }}>
              <div className="d-flex flex-wrap gap-1 align-items-center">
                {emailRecipients.map((email, index) => (
                  <span
                    key={index}
                    className="badge bg-primary d-flex align-items-center gap-1"
                    style={{ fontSize: "12px" }}
                  >
                    {email}
                    <FontAwesomeIcon
                      icon={faTimes}
                      style={{ cursor: "pointer", fontSize: "10px" }}
                      onClick={() => removeEmailTag(email)}
                    />
                  </span>
                ))}
                <input
                  type="email"
                  className="border-0 flex-grow-1"
                  style={{ outline: "none", minWidth: "200px" }}
                  placeholder="Press ENTER to add email..."
                  value={currentEmailInput}
                  onChange={(e) => {
                    setCurrentEmailInput(e.target.value)
                    setEmailInputTouched(true)
                  }}
                  onKeyDown={handleEmailInputKeyDown}
                  onBlur={handleEmailInputBlur}
                />
              </div>
            </div>
            <small className="text-muted">Press Enter or comma to add multiple emails</small>
          </div>

          {/* Subject Field */}
          <div className="mb-3">
            <label className="form-label">Subject</label>
            <input
              type="text"
              className="form-control"
              name="subject"
              value={messageData.subject}
              onChange={handleMessageInputChange}
              placeholder="Enter email subject"
            />
          </div>

          {/* Body Field with CKEditor */}
          <div className="mb-3">
            <label className="form-label fw-bold mb-2">Body</label>
            {/* <div className="ckeditor-container">
              <CKEditor
                editor={ClassicEditor}
                data={messageData.body}
                onChange={handleBodyChange}
                config={{
                  toolbar: [
                    "heading",
                    "|",
                    "bold",
                    "italic",
                    "link",
                    "bulletedList",
                    "numberedList",
                    "|",
                    "outdent",
                    "indent",
                    "|",
                    "blockQuote",
                    "undo",
                    "redo",
                  ],
                }}
              />
            </div> */}
            <div className="quill-container">
              <ReactQuill
                theme="snow"
                value={messageData.body}
                onChange={(value) =>
                  setMessageData((prev) => ({
                    ...prev,
                    body: value,
                  }))
                }
                modules={{
                  toolbar: [
                    [{ header: [1, 2, false] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link", "blockquote", "code-block"],
                    ["clean"],
                  ],
                }}
              />
            </div>

            <style jsx>{`
              .ckeditor-container {
                border: 1px solid #ddd;
                border-radius: 4px;
                overflow: hidden;
              }
              .ck.ck-toolbar {
                background: #f8f9fa;
                border: none !important;
                border-bottom: 1px solid #eee !important;
              }
              .ck.ck-content {
                min-height: 150px;
                padding: 15px !important;
                font-family: inherit;
                line-height: 1.5;
              }
              .ck.ck-button {
                padding: 5px 8px !important;
              }
            `}</style>
          </div>

          {/* Attachment Field */}
          <div className="mb-3">
            <label className="form-label">Attachment</label>
            <input
              type="file"
              className="form-control"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
            />
          </div>

          {/* Syntax Notes Table */}
          <div className="col-md-12 mt-4">
            <h3 className="h5 mb-2">Available Syntax</h3>
            <Table bordered size="sm">
              <thead>
                <tr>
                  <th>SYNTAX</th>
                  <th>FIELD</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{"{{contactName}}"}</td>
                  <td>Recipient Contact Name</td>
                </tr>
                <tr>
                  <td>{"{{sender_name}}"}</td>
                  <td>Sender Name</td>
                </tr>
                <tr>
                  <td>{"{{sender_company_name}}"}</td>
                  <td>Sender Company Name</td>
                </tr>
                <tr>
                  <td>{"{{sender_number}}"}</td>
                  <td>Sender Phone Number</td>
                </tr>
                <tr>
                  <td>{"{{sender_email}}"}</td>
                  <td>Sender Email Address</td>
                </tr>
                <tr>
                  <td>{"{{companyName}}"}</td>
                  <td>Company Name</td>
                </tr>
                <tr>
                  <td>{"{{leadOwnerName}}"}</td>
                  <td>Lead Owner Name</td>
                </tr>
                <tr>
                  <td>{"{{requirement}}"}</td>
                  <td>Requirement</td>
                </tr>
                <tr>
                  <td>{"{{budget}}"}</td>
                  <td>Budget</td>
                </tr>
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-default-outline" onClick={handleCloseModal} disabled={dataSendingLoader}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-default"
            onClick={handleSendMessage}
            disabled={dataSendingLoader || emailRecipients.length === 0}
          >
            {dataSendingLoader ? "Sending..." : "Send Email"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Integration Error Modal */}
      <Modal show={showIntegrationModal} onHide={handleIntegrationModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Email Integration Required</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <div className="mb-3">
            <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: "48px" }}></i>
          </div>
          <h5>SMTP Configuration Missing</h5>
          <p className="mb-3">
            Email integration is not configured. Please contact your administrator to set up SMTP settings.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => {
              // Navigate to integrations page
              if (extras?.navigate) {
                extras.navigate("/settings/integrations")
              }
              handleIntegrationModalClose()
            }}
          >
            Go to Integration Settings
          </button>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={handleIntegrationModalClose}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}